import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Modal } from 'components/ui/Modal'
import { offersApi, type Offer } from 'lib/api'
import { getStatusColor, formatDate } from 'lib/utils'
import { translateFrToEn } from 'lib/translationService'
import { Plus, Search, Trash2, FileText, ClipboardList, Clock, Edit3, Archive, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Upload, Brain, CheckCircle2, Loader2, Users, CheckSquare, CheckCircle2 as CheckCircle, X, MoreVertical, AlertTriangle } from 'lucide-react'
import { ImportOfferModal } from 'features/offers/components/ImportOfferModal'
import { CloneOfferModal } from 'features/offers/components/CloneOfferModal'
import { AIMatchingModal } from 'features/offers/components/AIMatchingModal'
import {
  OffersTableRowMenu,
  type OfferMenuAction,
  type OfferMenuItem,
} from 'features/offers/components/OffersTableRowMenu'
import { RecommendedSkillsModal, type RecommendedSkillsData } from 'features/offers/components/RecommendedSkillsModal'
import { buildRecommendedSkillsDataFromAiResponse } from 'lib/offerRecommendedSkillsUtils'
import { useToast } from 'contexts/ToastContext'
import { useAuth } from 'contexts/AuthContext'
import { PageHeader } from 'components/layout/PageHeader'
import { cn } from 'lib/utils'

type OfferStatusFilter = 'all' | 'draft' | 'published' | 'offer_closed' | 'closed'

export function Offers() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { addToast } = useToast()
  const { hasPermission, user } = useAuth()

  // Check if user is an organization admin
  const isOrgAdmin = user?.is_staff && !user?.is_superuser && user?.organization

  // Helper to check if user can create offers (has permission OR is org admin)
  const canCreateOffers = hasPermission('create_offers') || isOrgAdmin
  // Helper to check if user can edit offers (has permission OR is org admin)
  const canEditOffers = hasPermission('edit_offers') || isOrgAdmin
  // Helper to check if user can delete offers (has permission OR is org admin)
  const canDeleteOffers = hasPermission('delete_offers') || isOrgAdmin
  const [offers, setOffers] = useState<Offer[]>([])
  const [totalItems, setTotalItems] = useState<number>(0)
  const [offerStats, setOfferStats] = useState({
    total_offers: 0,
    published_offers: 0,
    draft_offers: 0,
    offer_closed_offers: 0,
    closed_offers: 0,
    expired_offers: 0,
  })
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showImportOffer, setShowImportOffer] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingOffer, setDeletingOffer] = useState<Offer | null>(null)
  const [showPublishConfirm, setShowPublishConfirm] = useState(false)
  const [showZeroWeightsWarning, setShowZeroWeightsWarning] = useState(false)
  const [publishingConfirmOffer, setPublishingConfirmOffer] = useState<Offer | null>(null)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [archivingConfirmOffer, setArchivingConfirmOffer] = useState<Offer | null>(null)
  const [showCloseConfirm, setShowCloseConfirm] = useState(false)
  const [closingConfirmOffer, setClosingConfirmOffer] = useState<Offer | null>(null)
  const [showAIMatching, setShowAIMatching] = useState(false)
  const [matchingOffer, setMatchingOffer] = useState<Offer | null>(null)
  const [minimumScore, setMinimumScore] = useState<number>(70)
  const [publishingOfferIds, setPublishingOfferIds] = useState<number[]>([])
  const [cloningOfferIds, setCloningOfferIds] = useState<number[]>([])
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [cloningOffer, setCloningOffer] = useState<Offer | null>(null)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedOffers, setSelectedOffers] = useState<Set<number>>(new Set())
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const [deletingBulk, setDeletingBulk] = useState(false)
  const [translatedTitles, setTranslatedTitles] = useState<Record<number, string>>({})
  const translatedTitleSources = useRef<Record<number, string>>({})
  const translatingOffers = useRef<Set<number>>(new Set())
  const [openMenuOfferId, setOpenMenuOfferId] = useState<number | null>(null)
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null)
  const [showRecommendedSkillsModal, setShowRecommendedSkillsModal] = useState(false)
  const [recommendedSkillsData, setRecommendedSkillsData] = useState<RecommendedSkillsData>({})
  const [activeOfferForSkills, setActiveOfferForSkills] = useState<Offer | null>(null)
  const [isSavingRecommendedSkills, setIsSavingRecommendedSkills] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  // Update minimum score from matchingOffer when it changes 
  useEffect(() => {
    if (matchingOffer?.minimum_score !== undefined) {
      setMinimumScore(matchingOffer.minimum_score)
    } else if (matchingOffer && matchingOffer.minimum_score === undefined) {
      setMinimumScore(70)
    }
  }, [matchingOffer])

  // Function to update minimum score via API
  const updateMinimumScore = async (newScore: number): Promise<void> => {
    if (!matchingOffer) return

    // Validate and convert to integer
    const numValue = Number(newScore)
    if (isNaN(numValue) || !isFinite(numValue)) {
      console.error('Invalid minimum score value:', newScore)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.invalidScore', 'Invalid score value')
      })
      return
    }

    // Convert to integer and clamp between 0 and 100
    const validatedScore = Math.max(0, Math.min(100, Math.round(numValue)))

    // Store the previous score to restore it in case of error
    const previousScore = minimumScore

    try {
      const response = await offersApi.update(matchingOffer.id, {
        minimum_score: validatedScore
      })
      // Update the offer in the list
      setOffers(prevOffers =>
        prevOffers.map(offer =>
          offer.id === matchingOffer.id ? response.data : offer
        )
      )
      // Update matchingOffer state
      setMatchingOffer(response.data)
      setMinimumScore(validatedScore)
    } catch (error: any) {
      console.error('Error updating minimum score:', error)

      // Restore the previous score
      setMinimumScore(previousScore)

      // Extract error message from backend response
      let errorMessage = t('offerDetail.minimumScoreUpdateFailed', 'Failed to update minimum score')

      if (error?.response?.data) {
        const errorData = error.response.data
        // Check for minimum_score specific error
        if (errorData.minimum_score) {
          let backendMessage = ''
          if (Array.isArray(errorData.minimum_score)) {
            backendMessage = errorData.minimum_score[0]
          } else if (typeof errorData.minimum_score === 'string') {
            backendMessage = errorData.minimum_score
          }
          // Extract the score from the backend message and show simple message
          const scoreMatch = backendMessage.match(/(\d+)%/)
          const score = scoreMatch ? scoreMatch[1] : minimumScore || 70
          errorMessage = `Minimum score cannot exceed ${score}%`
        } else if (errorData.detail) {
          errorMessage = errorData.detail
        } else if (errorData.error) {
          errorMessage = errorData.error
        } else if (typeof errorData === 'string') {
          errorMessage = errorData
        }
      } else if (error?.message) {
        errorMessage = error.message
      }

      // Show toast for all errors, including validation errors
      // The caller (Kanban or AIMatchingModal) can also show additional messages if needed
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: errorMessage
      })

      // Re-throw the error so the caller can handle it
      throw error
    }
  }

  const fetchOffers = async () => {
    try {
      setLoading(true)
      const params: any = {
        page: currentPage,
        page_size: itemsPerPage
      }
      if (searchTerm) params.search = searchTerm
      if (statusFilter !== 'all') params.status = statusFilter
      const [response, statsResponse] = await Promise.all([
        offersApi.getAll(params),
        offersApi.getStats()
      ])
      setOffers(response.data.results || [])
      setTotalItems(response.data.count || 0)
      setOfferStats(statsResponse.data)
    } catch (error: any) {
      if (error.response?.status === 404 && currentPage > 1) {
        // If a page is requested that is out of bounds, Fallback to page 1
        setCurrentPage(1)
      } else {
        console.error('Error fetching offers data:', error)
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => {
    if (fetchTimeout.current) clearTimeout(fetchTimeout.current)
    fetchTimeout.current = setTimeout(() => {
      fetchOffers()
    }, 300)
    return () => {
      if (fetchTimeout.current) clearTimeout(fetchTimeout.current)
    }
  }, [currentPage, searchTerm, statusFilter, itemsPerPage])

  // Helper function to get translated job title for an offer
  const getOfferJobTitle = (offer: Offer) => {
    if (
      i18n.language === 'en'
      && translatedTitles[offer.id]
      && translatedTitleSources.current[offer.id] === offer.job_title
    ) {
      return translatedTitles[offer.id]
    }
    return offer.job_title
  }

  // Pagination logic
  const currentOffers = offers
  const totalPages = Math.ceil(totalItems / itemsPerPage)

  // Translate offer titles one by one when language changes to English or page changes
  useEffect(() => {
    const isEnglish = i18n.language === 'en'
    if (!isEnglish) {
      if (Object.keys(translatedTitles).length > 0) {
        setTranslatedTitles({})
      }
      translatedTitleSources.current = {}
      translatingOffers.current.clear()
      return
    }

    let cancelled = false
    const translateTitles = async () => {
      // Calculate current page offers
      const startIdx = (currentPage - 1) * itemsPerPage
      const endIdx = startIdx + itemsPerPage
      const offersToTranslate = offers

      // Translate only the current page offers, one by one
      for (const offer of offersToTranslate) {
        if (cancelled) return
        // Skip if already translated for the current source title or currently translating
        if (
          !offer.job_title
          || (
            translatedTitles[offer.id]
            && translatedTitleSources.current[offer.id] === offer.job_title
          )
          || translatingOffers.current.has(offer.id)
        ) {
          continue
        }

        translatingOffers.current.add(offer.id)

        try {
          const translated = await translateFrToEn(offer.job_title)
          if (cancelled) return
          translatedTitleSources.current[offer.id] = offer.job_title
          setTranslatedTitles(prev => ({ ...prev, [offer.id]: translated }))
          // Small delay to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 100))
        } catch (error) {
          console.error('Translation error for offer:', error)
          if (cancelled) return
          setTranslatedTitles(prev => ({ ...prev, [offer.id]: offer.job_title }))
          translatedTitleSources.current[offer.id] = offer.job_title
        } finally {
          translatingOffers.current.delete(offer.id)
        }
      }
    }

    translateTitles()
    return () => {
      cancelled = true
    }
  }, [i18n.language, currentPage, offers, itemsPerPage, translatedTitles])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const normalizeStageLabel = (stage?: string | null) => {
    if (!stage) return null
    return stage
      .replace(/pre[-\s]?selection/gi, 'pré-sélection')
      .replace(/^interview$/i, 'Entretien')
  }

  const getCandidateStageLabel = (stage?: Offer['latest_stage'] | null) => {
    if (!stage) return null
    const key = `offers.candidateStages.${stage}`
    const translation = t(key, stage)
    return normalizeStageLabel(translation || stage)
  }

  const getCandidateSummary = (offer: Offer) => {
    const byStep = offer.candidates_by_step ?? []
    const totalProcessed = byStep.length > 0 ? byStep.reduce((sum, s) => sum + s.count, 0) : (offer.total_candidates ?? 0)
    const stageLabel = getCandidateStageLabel(offer.latest_stage)
    const stageCount = offer.latest_stage_count ?? 0
    const lastStep = byStep.length > 0 ? byStep[byStep.length - 1] : null
    return { totalProcessed, stageLabel, stageCount, lastStep }
  }

  const renderCandidateSummary = (offer: Offer) => {
    const { totalProcessed, stageLabel, stageCount, lastStep } = getCandidateSummary(offer)

    if (totalProcessed === 0) {
      return (
        <div className="inline-flex items-center gap-2 text-muted-foreground">
          <Users className="h-4 w-4" />
          <span className="text-sm">0</span>
        </div>
      )
    }

    return (
      <div className="inline-flex flex-wrap items-center gap-2">
        {/* Total (excluding rejected) with icon */}
        <div className="inline-flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1">
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-base font-bold text-foreground">{totalProcessed}</span>
        </div>
        {/* Only the latest step badge (e.g. 1 in Reboot Interview) */}
        {lastStep ? (
          <div className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50/80 dark:border-blue-800 dark:bg-blue-950/50 px-1.5 py-0.5">
            <div className="h-1.5 w-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
            <span className="text-[10px] font-medium text-blue-700 dark:text-blue-300 leading-tight">
              {t('offers.table.candidatesStageCompact', {
                count: lastStep.count,
                stage: normalizeStageLabel(lastStep.step_name),
                defaultValue: '{{count}} in {{stage}}'
              })}
            </span>
          </div>
        ) : stageLabel && stageCount > 0 ? (
          <div className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50/80 dark:border-blue-800 dark:bg-blue-950/50 px-1.5 py-0.5">
            <div className="h-1.5 w-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
            <span className="text-[10px] font-medium text-blue-700 dark:text-blue-300 leading-tight">
              {t('offers.table.candidatesStageCompact', {
                count: stageCount,
                stage: stageLabel,
                defaultValue: '{{count}} in {{stage}}'
              })}
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground italic">
            {t('offers.table.candidatesStageFallback', 'No active stage')}
          </span>
        )}
      </div>
    )
  }

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter])

  const handleView = (offer: Offer) => {
    if (selectionMode && canDeleteOffers) {
      toggleSelectOffer(offer.id)
    } else {
      navigate(`/offers/${offer.id}`)
    }
  }

  const applyStatusFilterFromStatCard = (filter: OfferStatusFilter) => {
    setStatusFilter(filter)
  }

  const statCardClassName = (filter: OfferStatusFilter) =>
    cn(
      'cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
      statusFilter === filter && 'ring-2 ring-[#19A6A6] border-[#19A6A6]/40 shadow-sm'
    )

  const handleStatCardKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    filter: OfferStatusFilter
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      applyStatusFilterFromStatCard(filter)
    }
  }

  const handleRowKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    offer: Offer
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleView(offer)
    }
  }

  const closeOfferMenu = () => {
    setOpenMenuOfferId(null)
    setMenuAnchorRect(null)
  }

  const openOfferMenu = (offerId: number, anchor: HTMLElement) => {
    setOpenMenuOfferId(offerId)
    setMenuAnchorRect(anchor.getBoundingClientRect())
  }

  const getOfferMenuItems = (offer: Offer): OfferMenuItem[] => {
    const items: OfferMenuItem[] = [
      { action: 'view', label: t('settings.emailTemplatesFigmaPage.menu.view') },
    ]
    if (canEditOffers && offer.status !== 'published' && offer.status !== 'offer_closed' && offer.status !== 'closed') {
      items.push({ action: 'edit', label: t('settings.emailTemplatesFigmaPage.menu.edit') })
    }
    if (canEditOffers && offer.status === 'published') {
      items.push({ action: 'close', label: t('offers.tableMenu.close', 'Close') })
    }
    if (canEditOffers && (offer.status === 'draft' || offer.status === 'published' || offer.status === 'offer_closed')) {
      items.push({ action: 'archive', label: t('offers.tableMenu.archive') })
    }
    if (canCreateOffers) {
      items.push({ action: 'clone', label: t('offers.tableMenu.clone') })
    }
    if (canDeleteOffers) {
      items.push({
        action: 'delete',
        label: t('settings.emailTemplatesFigmaPage.menu.delete'),
        tone: 'danger',
      })
    }
    return items
  }

  const handleOfferMenuAction = (offer: Offer, action: OfferMenuAction) => {
    switch (action) {
      case 'view':
        navigate(`/offers/${offer.id}`)
        break
      case 'edit':
        handleEditOffer(offer)
        break
      case 'close':
        handleCloseOffer(offer)
        break
      case 'archive':
        handleArchiveOffer(offer)
        break
      case 'clone':
        openCloneModal(offer)
        break
      case 'delete':
        openDeleteConfirm(offer)
        break
    }
  }

  const renderOfferMenuTrigger = (offer: Offer) => {
    if (getOfferMenuItems(offer).length === 0) return null
    return (
      <button
        type="button"
        className="text-[#7F7F7F] dark:text-muted-foreground hover:text-foreground shrink-0"
        onClick={(event) => {
          event.stopPropagation()
          openOfferMenu(offer.id, event.currentTarget)
        }}
        aria-label={t('offers.actions.moreActions', 'More actions')}
      >
        <MoreVertical className="h-4 w-4" />
      </button>
    )
  }

  const toggleSelectOffer = (offerId: number) => {
    setSelectedOffers(prev => {
      const next = new Set(prev)
      if (next.has(offerId)) {
        next.delete(offerId)
      } else {
        next.add(offerId)
      }
      return next
    })
  }

  const toggleSelectAllOffers = () => {
    setSelectedOffers(prev => {
      if (prev.size === currentOffers.length) {
        return new Set()
      }
      return new Set(currentOffers.map(o => o.id))
    })
  }

  const toggleSelectionMode = () => {
    setSelectionMode(prev => {
      const next = !prev
      if (!next) {
        setSelectedOffers(new Set())
      }
      return next
    })
  }

  const handleBulkDeleteOffers = async () => {
    if (selectedOffers.size === 0) return

    setDeletingBulk(true)
    const idsToDelete = Array.from(selectedOffers)
    let successCount = 0

    try {
      for (const id of idsToDelete) {
        try {
          await offersApi.delete(id)
          successCount += 1
        } catch (error) {
          console.error('Error deleting offer in bulk:', error)
        }
      }

      if (successCount > 0) {
        setOffers(prev => prev.filter(o => !selectedOffers.has(o.id)))
        addToast({
          type: 'success',
          title: t('common.success'),
          message: t('offers.bulkDeleteSuccess', { count: successCount, defaultValue: `${successCount} offers deleted` }),
          duration: 4000
        })
      } else {
        addToast({
          type: 'error',
          title: t('common.error'),
          message: t('offers.bulkDeleteFailed', 'Failed to delete selected offers. Please try again.'),
          duration: 5000
        })
      }

      setSelectedOffers(new Set())
      setSelectionMode(false)
      setShowBulkDeleteModal(false)
    } finally {
      setDeletingBulk(false)
    }
  }

  const handleDeleteOffer = async () => {
    if (!deletingOffer) return

    try {
      await offersApi.delete(deletingOffer.id)
      setOffers(offers.filter(o => o.id !== deletingOffer.id))
      setShowDeleteConfirm(false)
      setDeletingOffer(null)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('offers.messages.offerDeleted'),
        duration: 3000
      })
    } catch (error) {
      console.error('Error deleting offer:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offers.messages.offerDeleteFailed'),
        duration: 5000
      })
    }
  }

  // Validate if offer can be published (same requirements as create page)
  const canPublishOffer = (offer: Offer): { valid: boolean; errors: string[] } => {
    const errors: string[] = []

    // Validate job_title: required, min 3, max 100 characters
    if (!offer.job_title || offer.job_title.trim().length === 0) {
      errors.push(t('createOffer.validation.jobTitleRequired', 'Job title is required'))
    } else if (offer.job_title.trim().length < 3) {
      errors.push(t('createOffer.validation.jobTitleMin', 'Job title must be at least 3 characters'))
    } else if (offer.job_title.trim().length > 100) {
      errors.push(t('createOffer.validation.jobTitleMax', 'Job title must be at most 100 characters'))
    }

    // Validate description_text: required, min 50 characters (after stripping HTML)
    const descriptionText = offer.description_text || offer.description || ''
    const textContent = descriptionText.replace(/<[^>]*>/g, '').trim()

    if (!descriptionText || textContent.length === 0) {
      errors.push(t('createOffer.validation.descriptionRequired', 'Description is required'))
    } else if (textContent.length < 50) {
      errors.push(t('createOffer.validation.descriptionMin', 'Description must be at least 50 characters'))
    }

    // Validate ai_matching_weights: sum of weights cannot be 0
    const weights = offer.ai_matching_weights || {}
    const sumWeights = Object.values(weights).reduce((sum: number, val: any) => sum + (Number(val) || 0), 0)
    if (sumWeights === 0) {
      errors.push(t('offers.messages.cannotPublishZeroWeights', "Il n’est pas possible d’activer l’offre si tous les poids des champs sont à 0, Au moins un poids doit être différent de 0. Veuillez d'abord modifier les poids avant d'activer l'offre."))
    }

    return {
      valid: errors.length === 0,
      errors
    }
  }

  const handlePublishOffer = async (offer: Offer) => {
    try {
      // Fetch the full offer before validation
      const fullOfferOption = await offersApi.getById(offer.id)
      const fullOffer = fullOfferOption.data
      // Validate offer before showing confirmation
      const validation = canPublishOffer(fullOffer)
      if (!validation.valid) {
        const weights = fullOffer.ai_matching_weights || {}
        const sumWeights = Object.values(weights).reduce((sum: number, val: any) => sum + (Number(val) || 0), 0)
        if (sumWeights === 0) {
          setShowZeroWeightsWarning(true)
          return
        }
        addToast({
          type: 'error',
          title: t('offers.messages.cannotPublish', 'Cannot publish offer'),
          message: validation.errors.join(', '),
          duration: 5000
        })
        return
      }
      // Show confirmation modal
      setPublishingConfirmOffer(fullOffer)
      setShowPublishConfirm(true)
    } catch (error) {
      console.error('Error fetching full offer for validation:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offers.messages.fetchError', 'Error fetching offer details'),
        duration: 5000
      })
    }
  }

  const handleAcceptRecommendedSkills = async (
    selected: {
      hard_skills_non_mentionnées: string[]
      soft_skills_non_mentionnées: string[]
      nice_to_have_non_mentionnées: string[]
    },
    weights?: {
      hard_skills: number
      soft_skills: number
      nice_to_have: number
    },
    skillWeights?: Record<string, number>,
    lockedSkills?: string[]
  ) => {
    if (!activeOfferForSkills) return
    setIsSavingRecommendedSkills(true)
    try {
      const payload: Parameters<typeof offersApi.updateAiSkills>[1] = {
        requirements: {
          hard_skills_mentionnées: activeOfferForSkills.requirements?.hard_skills_mentionnées ?? [],
          hard_skills_non_mentionnées: selected.hard_skills_non_mentionnées
        },
        soft_skills: {
          soft_skills_mentionnées: activeOfferForSkills.soft_skills?.soft_skills_mentionnées ?? [],
          soft_skills_non_mentionnées: selected.soft_skills_non_mentionnées
        },
        benefits: {
          nice_to_have_mentionnées: activeOfferForSkills.benefits?.nice_to_have_mentionnées ?? [],
          nice_to_have_non_mentionnées: selected.nice_to_have_non_mentionnées
        }
      }
      if (weights) {
        payload.ai_matching_weights = weights
      }
      if (skillWeights) {
        const includedSkills = new Set([
          ...payload.requirements!.hard_skills_mentionnées,
          ...payload.requirements!.hard_skills_non_mentionnées,
          ...payload.soft_skills!.soft_skills_mentionnées,
          ...payload.soft_skills!.soft_skills_non_mentionnées,
          ...payload.benefits!.nice_to_have_mentionnées,
          ...payload.benefits!.nice_to_have_non_mentionnées,
        ])
        payload.skill_weights = Object.fromEntries(
          Object.entries(skillWeights).filter(([skill]) => includedSkills.has(skill))
        )
      }
      if (lockedSkills) {
        const filterMatching = { ...(activeOfferForSkills.filter_matching ?? {}) }
        const skillFilters = [
          { prefix: 'hardskill_', field: 'hard_skills' as const, similarity: 65 },
          { prefix: 'softskill_', field: 'soft_skills' as const, similarity: 30 },
          { prefix: 'nicetohave_', field: 'nice_to_have' as const, similarity: 20 },
        ]

        for (const { prefix, field, similarity } of skillFilters) {
          const lockedWeights = Object.fromEntries(
            lockedSkills
              .filter(skill => skill.startsWith(prefix))
              .map(skill => [skill.slice(prefix.length), similarity])
          )
          if (Object.keys(lockedWeights).length > 0) {
            filterMatching[field] = lockedWeights
          } else {
            delete filterMatching[field]
          }
        }
        payload.filter_matching = filterMatching
      }
      const response = await offersApi.updateAiSkills(activeOfferForSkills.id, payload)
      // Update the offer in the list
      setOffers(prev =>
        prev.map(o => (o.id === activeOfferForSkills.id ? response.data : o))
      )
      setShowRecommendedSkillsModal(false)
      setActiveOfferForSkills(null)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('recommendedSkills.successMessage', 'Les compétences sélectionnées ont été ajoutées avec succès à l’offre.')
      })
    } catch (err) {
      console.error('Error saving recommended skills:', err)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('recommendedSkills.errorMessage', 'Impossible d’enregistrer les compétences.')
      })
    } finally {
      setIsSavingRecommendedSkills(false)
    }
  }

  const confirmPublishOffer = async () => {
    if (!publishingConfirmOffer || publishingOfferIds.includes(publishingConfirmOffer.id)) return

    setPublishingOfferIds(prev => [...prev, publishingConfirmOffer.id])
    try {
      const response = await offersApi.update(publishingConfirmOffer.id, { status: 'published' })
      const updatedOffer = response.data
      setOffers(prev =>
        prev.map(o => (o.id === publishingConfirmOffer.id ? { ...o, status: 'published' } : o))
      )
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('offers.messages.offerPublished', 'Offer has been published successfully'),
        duration: 3000
      })
      setShowPublishConfirm(false)
      setPublishingConfirmOffer(null)

      // Show recommended skills modal if AI has extracted skills
      // The LLM runs synchronously, so ai_response is already in the PATCH response;
      // if not (e.g. serializer caching), do a GET to fetch fresh data
      let freshAiResponse = updatedOffer?.ai_response
      let freshOffer = updatedOffer
      if (!freshAiResponse) {
        try {
          const freshResponse = await offersApi.getById(publishingConfirmOffer.id)
          freshOffer = freshResponse.data
          freshAiResponse = freshResponse.data?.ai_response
        } catch {
          // ignore – modal is a bonus feature
        }
      }
      const recommendedSkills = buildRecommendedSkillsDataFromAiResponse(freshAiResponse)
      if (recommendedSkills && freshOffer) {
        setRecommendedSkillsData(recommendedSkills)
        setActiveOfferForSkills(freshOffer)
        setShowRecommendedSkillsModal(true)
      }
    } catch (error: any) {
      console.error('Error publishing offer:', error)

      let errorMessage = t('offers.messages.offerPublishFailed', 'Failed to publish offer')
      if (error?.response?.data) {
        const errorData = error.response.data
        if (typeof errorData === 'string') {
          errorMessage = errorData
        } else if (errorData.error) {
          errorMessage = errorData.error
        } else if (errorData.detail) {
          errorMessage = errorData.detail
        } else if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          const firstField = Object.keys(errorData)[0]
          if (firstField) {
            const fieldError = errorData[firstField]
            const msg = Array.isArray(fieldError) ? fieldError[0] : fieldError
            if (typeof msg === 'string' && msg.trim()) {
              errorMessage = msg
            }
          }
        }
      }

      addToast({
        type: 'error',
        title: t('common.error'),
        message: errorMessage,
        duration: 5000
      })
    } finally {
      setPublishingOfferIds(prev => prev.filter(id => id !== publishingConfirmOffer.id))
    }
  }

  const handleAIMatching = (offer: Offer) => {
    setMatchingOffer(offer)
    setShowAIMatching(true)
  }

  const openCloneModal = (offer: Offer) => {
    if (cloningOfferIds.includes(offer.id)) return
    setCloningOffer(offer)
    setShowCloneModal(true)
  }

  const closeCloneModal = () => {
    if (cloningOffer && cloningOfferIds.includes(cloningOffer.id)) return
    setShowCloneModal(false)
    setCloningOffer(null)
  }

  const confirmCloneOffer = async (offer: Offer, hiringTemplateId: number | null) => {
    if (cloningOfferIds.includes(offer.id)) return

    setCloningOfferIds(prev => [...prev, offer.id])
    try {
      const response = await offersApi.clone(offer.id, hiringTemplateId)
      const clonedOffer = response.data.offer

      setOffers(prev => [clonedOffer, ...prev])
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('offers.messages.offerCloned', 'Offer cloned successfully'),
        duration: 3000
      })
      setShowCloneModal(false)
      setCloningOffer(null)
      navigate(`/offers/${clonedOffer.id}`)
    } catch (error) {
      console.error('Error cloning offer:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offers.messages.offerCloneFailed', 'Failed to clone offer'),
        duration: 5000
      })
    } finally {
      setCloningOfferIds(prev => prev.filter(id => id !== offer.id))
    }
  }

  const openDeleteConfirm = (offer: Offer) => {
    setDeletingOffer(offer)
    setShowDeleteConfirm(true)
  }

  const handleEditOffer = (offer: Offer) => {
    navigate(`/offers/${offer.id}/edit`)
  }

  const handleArchiveOffer = (offer: Offer) => {
    // Show confirmation modal
    setArchivingConfirmOffer(offer)
    setShowArchiveConfirm(true)
  }

  const handleCloseOffer = (offer: Offer) => {
    setClosingConfirmOffer(offer)
    setShowCloseConfirm(true)
  }

  const confirmCloseOffer = async () => {
    if (!closingConfirmOffer) return

    try {
      await offersApi.update(closingConfirmOffer.id, { status: 'offer_closed' })
      setOffers(prev =>
        prev.map(o => (o.id === closingConfirmOffer.id ? { ...o, status: 'offer_closed' } : o))
      )
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('offers.messages.offerClosed', 'Offer has been closed successfully'),
        duration: 3000
      })
      setShowCloseConfirm(false)
      setClosingConfirmOffer(null)
      // Refresh stats cards
      try {
        const statsResponse = await offersApi.getStats()
        setOfferStats(statsResponse.data)
      } catch {
        // ignore stats refresh errors
      }
    } catch (error) {
      console.error('Error closing offer:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offers.messages.offerCloseFailed', 'Failed to close offer'),
        duration: 5000
      })
    }
  }

  const confirmArchiveOffer = async () => {
    if (!archivingConfirmOffer) return

    try {
      await offersApi.update(archivingConfirmOffer.id, { status: 'closed' })
      setOffers(prev =>
        prev.map(o => (o.id === archivingConfirmOffer.id ? { ...o, status: 'closed' } : o))
      )
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('offers.messages.offerArchived', 'Offer has been archived successfully'),
        duration: 3000
      })
      setShowArchiveConfirm(false)
      setArchivingConfirmOffer(null)
      try {
        const statsResponse = await offersApi.getStats()
        setOfferStats(statsResponse.data)
      } catch {
        // ignore stats refresh errors
      }
    } catch (error) {
      console.error('Error archiving offer:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offers.messages.offerArchiveFailed', 'Failed to archive offer'),
        duration: 5000
      })
    }
  }

  // Unarchive functionality removed - archived offers cannot change status

  return (
    <div className="space-y-4 px-4 py-4">
      <PageHeader
        title={t('offers.title')}
        actions={
          <>
            {canCreateOffers && (
              <Button
                onClick={() => navigate('/offers/create')}
                size="sm"
                className="border-transparent bg-[#19A6A6] text-white hover:bg-[#19A6A6]/90"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span>{t('offers.createOffer')}</span>
              </Button>
            )}
            {canCreateOffers && (
              <Button onClick={() => setShowImportOffer(true)} variant="outline" size="sm">
                <Upload className="h-4 w-4 mr-2" />
                <span>{t('offers.importOffer')}</span>
              </Button>
            )}
          </>
        }
      />

      {/* Status Overview — click to filter table */}
      <div className="grid gap-3 md:grid-cols-5 sm:grid-cols-2">
        <Card
          role="button"
          tabIndex={0}
          className={statCardClassName('all')}
          onClick={() => applyStatusFilterFromStatCard('all')}
          onKeyDown={(event) => handleStatCardKeyDown(event, 'all')}
          aria-pressed={statusFilter === 'all'}
        >
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
                <ClipboardList className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-muted-foreground">{t('offers.stats.totalOffers')}</p>
                <p className="text-xl font-bold">{offerStats.total_offers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          role="button"
          tabIndex={0}
          className={statCardClassName('published')}
          onClick={() => applyStatusFilterFromStatCard('published')}
          onKeyDown={(event) => handleStatCardKeyDown(event, 'published')}
          aria-pressed={statusFilter === 'published'}
        >
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900">
                <Clock className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-muted-foreground">{t('offers.stats.publishedOffers')}</p>
                <p className="text-xl font-bold">{offerStats.published_offers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          role="button"
          tabIndex={0}
          className={statCardClassName('draft')}
          onClick={() => applyStatusFilterFromStatCard('draft')}
          onKeyDown={(event) => handleStatCardKeyDown(event, 'draft')}
          aria-pressed={statusFilter === 'draft'}
        >
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900">
                <Edit3 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-muted-foreground">{t('offers.stats.draftOffers')}</p>
                <p className="text-xl font-bold">{offerStats.draft_offers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          role="button"
          tabIndex={0}
          className={statCardClassName('offer_closed')}
          onClick={() => applyStatusFilterFromStatCard('offer_closed')}
          onKeyDown={(event) => handleStatCardKeyDown(event, 'offer_closed')}
          aria-pressed={statusFilter === 'offer_closed'}
        >
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900">
                <Archive className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-muted-foreground">{t('offers.stats.offerClosedOffers')}</p>
                <p className="text-xl font-bold">{offerStats.offer_closed_offers ?? offerStats.expired_offers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          role="button"
          tabIndex={0}
          className={statCardClassName('closed')}
          onClick={() => applyStatusFilterFromStatCard('closed')}
          onKeyDown={(event) => handleStatCardKeyDown(event, 'closed')}
          aria-pressed={statusFilter === 'closed'}
        >
          <CardContent className="p-3">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                <Archive className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-muted-foreground">{t('offers.stats.closedOffers')}</p>
                <p className="text-xl font-bold">{offerStats.closed_offers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compact Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('offers.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          {canDeleteOffers && currentOffers.length > 0 && (
            <>
              <Button
                variant={selectionMode ? 'default' : 'outline'}
                size="sm"
                onClick={toggleSelectionMode}
                className="h-9 w-9 p-0 flex items-center justify-center transition-all"
                title={
                  selectionMode
                    ? t('offers.selection.cancel', 'Cancel selection')
                    : t('offers.selection.enable', 'Select offers')
                }
              >
                {selectionMode ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
              </Button>
              {selectionMode && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSelectAllOffers}
                    className="h-9 w-9 p-0 flex items-center justify-center transition-all"
                    title={
                      selectedOffers.size === currentOffers.length && currentOffers.length > 0
                        ? t('offers.selection.deselectAll', 'Deselect all')
                        : t('offers.selection.selectAll', 'Select all')
                    }
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                  {selectedOffers.size > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowBulkDeleteModal(true)}
                      className="h-9 w-9 p-0 flex items-center justify-center transition-all"
                      title={t('offers.selection.deleteSelected', { count: selectedOffers.size, defaultValue: 'Delete selected' })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            </>
          )}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex h-9 rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none"
          >
            <option value="all">{t('offers.status.all')}</option>
            <option value="draft">{t('offers.status.draft')}</option>
            <option value="published">{t('offers.status.published')}</option>
            <option value="offer_closed">{t('offers.status.offer_closed')}</option>
            <option value="closed">{t('offers.status.closed')}</option>
          </select>
          {(searchTerm || statusFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchTerm('')
                setStatusFilter('all')
              }}
            >
              {t('common.clearFilters', 'Clear filters')}
            </Button>
          )}
        </div>
      </div>

      {/* DataTable - Desktop View */}
      <Card>
        <CardContent className="p-0">
          {/* Mobile Card View */}
          <div className="block md:hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
              </div>
            ) : currentOffers.length === 0 ? (
              <div className="py-12 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t('offers.noResults')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {totalItems === 0
                    ? (searchTerm || statusFilter !== 'all'
                      ? t('offers.tryAdjustFilters')
                      : t('offers.noData'))
                    : t('offers.noOffersOnPage')
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-3 p-4">
                {currentOffers.map((offer) => {
                  const { totalProcessed: total, stageLabel, stageCount } = getCandidateSummary(offer)
                  return (
                    <Card
                      key={offer.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleView(offer)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          handleView(offer)
                        }
                      }}
                      className="border cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    >
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-2">
                                {getOfferJobTitle(offer)}
                              </h3>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className={`${getStatusColor(offer.status)} text-xs px-2 py-1`}>
                                  {t(`offers.status.${offer.status}`)}
                                </Badge>
                                {offer.status === 'draft' && canEditOffers && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      handlePublishOffer(offer)
                                    }}
                                    disabled={publishingOfferIds.includes(offer.id)}
                                    title={t('offers.actions.publishOffer', 'Publish offer')}
                                    className="h-7 w-7 p-0 bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 rounded-full"
                                  >
                                    {publishingOfferIds.includes(offer.id) ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col gap-2 text-sm">
                            <div>
                              <span className="font-medium text-foreground">
                                {t('offers.table.created', 'Created')}:
                              </span>{' '}
                              {offer.created_date ? formatDate(offer.created_date) : '—'}
                            </div>
                            <div className="flex items-center">
                              {renderCandidateSummary(offer)}
                            </div>
                          </div>

                          <div
                            className="flex items-center justify-end pt-2 border-t"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {renderOfferMenuTrigger(offer)}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block">
            <div className="overflow-hidden rounded-xl border bg-card">
              <table className="w-full table-fixed">
                <thead className="bg-muted/70 text-muted-foreground">
                  <tr>
                    {canDeleteOffers && selectionMode && (
                      <th className="w-10 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">
                        {/* empty header for selection column (only in selection mode) */}
                      </th>
                    )}
                    <th className="w-[35%] min-w-[35%] max-w-[35%] sm:w-[25%] sm:min-w-[25%] sm:max-w-[25%] lg:w-[35%] lg:min-w-[35%] lg:max-w-[35%] px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">
                      {t('offers.table.jobPosition')}
                    </th>
                    <th className="w-[13%] sm:w-[15%] lg:w-[13%] px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">
                      {t('offers.table.status')}
                    </th>
                    <th className="w-[13%] sm:w-[15%] lg:w-[13%] px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">
                      {t('offers.table.created', 'Created')}
                    </th>
                    <th className="w-[13%] sm:w-[15%] lg:w-[13%] px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">
                      {t('offers.table.candidates', 'Candidates')}
                    </th>
                    <th className="w-8 min-w-8 max-w-8 px-2 py-2.5" aria-hidden />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    <tr>
                      <td colSpan={canDeleteOffers && selectionMode ? 6 : 5} className="py-12 text-center">
                        <div className="flex items-center justify-center">
                          <Loader2 className="h-7 w-7 animate-spin text-primary" />
                        </div>
                      </td>
                    </tr>
                  ) : currentOffers.length === 0 ? (
                    <tr>
                      <td colSpan={canDeleteOffers && selectionMode ? 6 : 5} className="py-12 text-center">
                        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">{t('offers.noResults')}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {totalItems === 0
                            ? (searchTerm || statusFilter !== 'all'
                              ? t('offers.tryAdjustFilters')
                              : t('offers.noData'))
                            : t('offers.noOffersOnPage')
                          }
                        </p>
                      </td>
                    </tr>
                  ) : (
                    currentOffers.map((offer) => (
                      <tr
                        key={offer.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleView(offer)}
                        onKeyDown={(event) => handleRowKeyDown(event, offer)}
                        className={`cursor-pointer hover:bg-muted/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${selectionMode && selectedOffers.has(offer.id) ? 'bg-primary/5' : ''
                          }`}
                      >
                        {canDeleteOffers && selectionMode && (
                          <td className="px-3 py-3 align-middle">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleSelectOffer(offer.id)
                              }}
                              className={`h-5 w-5 rounded border flex items-center justify-center transition-colors ${selectedOffers.has(offer.id)
                                ? 'bg-primary border-primary text-primary-foreground'
                                : 'border-border text-muted-foreground hover:border-primary/60'
                                }`}
                              aria-pressed={selectedOffers.has(offer.id)}
                            >
                              {selectedOffers.has(offer.id) && <CheckCircle className="h-3 w-3" />}
                            </button>
                          </td>
                        )}
                        <td className="w-[35%] min-w-[35%] max-w-[35%] sm:w-[25%] sm:min-w-[25%] sm:max-w-[25%] lg:w-[35%] lg:min-w-[35%] lg:max-w-[35%] px-3 py-3 align-middle">
                          <span className="block w-full truncate font-semibold text-sm text-foreground">
                            {getOfferJobTitle(offer)}
                          </span>
                        </td>
                        <td className="px-3 py-3 align-middle">
                          <div className="inline-flex items-center gap-1.5 rounded-full border border-border/80 px-2.5 py-0.5 bg-background">
                            <span
                              className={`h-2.5 w-2.5 rounded-full ${getStatusColor(offer.status)} bg-current`}
                            ></span>
                            <span className="text-xs text-foreground">
                              {t(`offers.status.${offer.status}`)}
                            </span>
                            {offer.status === 'draft' && hasPermission('edit_offers') && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  handlePublishOffer(offer)
                                }}
                                disabled={publishingOfferIds.includes(offer.id)}
                                title={t('offers.actions.publishOffer', 'Publish offer')}
                                className="h-6 w-6 p-0 bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 rounded-full"
                                aria-label={t('offers.actions.publishOffer', 'Publish offer')}
                              >
                                {publishingOfferIds.includes(offer.id) ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="h-3 w-3" />
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-middle text-sm text-muted-foreground">
                          {offer.created_date ? formatDate(offer.created_date) : '—'}
                        </td>
                        <td className="px-3 py-3 align-middle">
                          {renderCandidateSummary(offer)}
                        </td>
                        <td
                          className="w-8 min-w-8 max-w-8 px-2 py-3 align-middle"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex justify-end">
                            {renderOfferMenuTrigger(offer)}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Pagination */}
      {
        totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-3 border-t">
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                title={t('common.firstPage', 'First page')}
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title={t('common.previous', 'Previous')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center px-3 min-w-[3rem] justify-center">
              <span className="text-xs font-bold text-foreground">
                {currentPage}/{totalPages}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title={t('common.next', 'Next')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                title={t('common.lastPage', 'Last page')}
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )
      }

      {/* Bulk Delete Modal */}
      <Modal
        isOpen={showBulkDeleteModal}
        onClose={() => {
          if (!deletingBulk) setShowBulkDeleteModal(false)
        }}
        title={t('offers.bulkDelete.title', 'Delete Multiple Offers')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              'offers.bulkDelete.message',
              {
                count: selectedOffers.size,
                defaultValue: `Are you sure you want to delete ${selectedOffers.size} offer(s)? This action cannot be undone.`
              }
            )}
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteModal(false)}
              disabled={deletingBulk}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeleteOffers}
              disabled={deletingBulk}
            >
              {deletingBulk ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('common.deleting', 'Deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('offers.bulkDelete.confirm', 'Delete All')}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Clone Offer Modal */}
      <CloneOfferModal
        isOpen={showCloneModal}
        offer={cloningOffer}
        onClose={closeCloneModal}
        onConfirm={confirmCloneOffer}
        isCloning={cloningOffer != null && cloningOfferIds.includes(cloningOffer.id)}
      />

      {/* Import Offer Modal */}
      {
        showImportOffer && (
          <ImportOfferModal
            isOpen={showImportOffer}
            onClose={() => setShowImportOffer(false)}
            onOfferImported={() => {
              setShowImportOffer(false)
              fetchOffers() // Refresh the offers list
            }}
          />
        )
      }

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false)
          setDeletingOffer(null)
        }}
        title={t('offers.deleteOfferTitle', 'Delete Offer')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('offers.deleteOfferConfirm', { title: deletingOffer?.job_title })}
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false)
                setDeletingOffer(null)
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteOffer}
            >
              {t('offers.deleteOfferCta', 'Delete Offer')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Publish Confirmation Modal */}
      <Modal
        isOpen={showPublishConfirm}
        onClose={() => {
          setShowPublishConfirm(false)
          setPublishingConfirmOffer(null)
        }}
        title={t('offers.confirmPublish.title', 'Publish Offer')}
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-foreground mb-3">
              {t('offers.confirmPublish.message', 'Are you sure you want to publish this offer?')}
            </p>
            <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md p-3">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-2">
                {t('offers.confirmPublish.warning', 'Important:')}
              </p>
              <ul className="text-xs text-amber-800 dark:text-amber-200 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>{t('offers.confirmPublish.cannotEdit', 'Once published, this offer cannot be edited')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>{t('offers.confirmPublish.cannotDraft', 'You cannot return it to draft status')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>{t('offers.confirmPublish.onlyArchive', 'You can only archive it afterwards')}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPublishConfirm(false)
                setPublishingConfirmOffer(null)
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={confirmPublishOffer}
              disabled={publishingOfferIds.includes(publishingConfirmOffer?.id || 0)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {publishingOfferIds.includes(publishingConfirmOffer?.id || 0) ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('common.publishing', 'Publishing')}
                </>
              ) : (
                t('offers.confirmPublish.confirm', 'Publish Offer')
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Zero Weights Warning Modal */}
      <Modal
        isOpen={showZeroWeightsWarning}
        onClose={() => setShowZeroWeightsWarning(false)}
        title={t('common.warning', 'Warning')}
      >
        <div className="space-y-4">
          <div className="flex items-start space-x-3 text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-medium">
              {t('offers.messages.cannotPublishZeroWeights', "Il n’est pas possible d’activer l’offre si tous les poids des champs sont à 0, Au moins un poids doit être différent de 0. Veuillez d'abord modifier les poids avant d'activer l'offre.")}
            </p>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => setShowZeroWeightsWarning(false)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {t('common.ok', 'OK')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Close (Clôturer) Confirmation Modal */}
      <Modal
        isOpen={showCloseConfirm}
        onClose={() => {
          setShowCloseConfirm(false)
          setClosingConfirmOffer(null)
        }}
        title={t('offers.confirmClose.title', 'Close Offer')}
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-foreground mb-3">
              {t('offers.confirmClose.message', 'Are you sure you want to close this offer?')}
            </p>
            <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-md p-3">
              <p className="text-sm font-medium text-orange-900 dark:text-orange-100 mb-2">
                {t('offers.confirmClose.note', 'Note:')}
              </p>
              <ul className="text-xs text-orange-800 dark:text-orange-200 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>{t('offers.confirmClose.noCandidates', 'No new candidates can be added while the offer is closed')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>{t('offers.confirmClose.canReopen', 'You will not be able to reactivate it, only archive it permanently')}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowCloseConfirm(false)
                setClosingConfirmOffer(null)
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={confirmCloseOffer}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {t('offers.confirmClose.confirm', 'Close Offer')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal
        isOpen={showArchiveConfirm}
        onClose={() => {
          setShowArchiveConfirm(false)
          setArchivingConfirmOffer(null)
        }}
        title={t('offers.confirmArchive.title', 'Archive Offer')}
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-foreground mb-3">
              {t('offers.confirmArchive.message', 'Are you sure you want to archive this offer?')}
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md p-3">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                {t('offers.confirmArchive.note', 'Note:')}
              </p>
              <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <span>{t('offers.confirmArchive.permanent', 'Archived offers cannot change status')}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowArchiveConfirm(false)
                setArchivingConfirmOffer(null)
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmArchiveOffer}
            >
              {t('offers.confirmArchive.confirm', 'Archive Offer')}
            </Button>
          </div>
        </div>
      </Modal>
      {/* AI Matching Modal */}
      {
        showAIMatching && matchingOffer && (
          <AIMatchingModal
            isOpen={showAIMatching}
            onClose={() => {
              setShowAIMatching(false)
              setMatchingOffer(null)
            }}
            offerId={matchingOffer.id}
            offerTitle={matchingOffer.job_title}
            offerStatus={matchingOffer.status}
            minimumScore={minimumScore}
            onMinimumScoreChange={updateMinimumScore}
            onScoresUpdated={fetchOffers}
            onOfferPublished={() => {
              // Refresh offers list after publishing
              fetchOffers()
              // Update matchingOffer status if it's the same offer
              if (matchingOffer) {
                setMatchingOffer(prev => prev ? { ...prev, status: 'published' } : null)
              }
            }}
          />
        )
      }

      {openMenuOfferId !== null && menuAnchorRect && (() => {
        const menuOffer = offers.find((item) => item.id === openMenuOfferId)
        if (!menuOffer) return null
        return (
          <OffersTableRowMenu
            anchorRect={menuAnchorRect}
            items={getOfferMenuItems(menuOffer)}
            onClose={closeOfferMenu}
            onAction={(action) => handleOfferMenuAction(menuOffer, action)}
          />
        )
      })()}

      {/* Recommended Skills Modal – shown after offer activation */}
      <RecommendedSkillsModal
        isOpen={showRecommendedSkillsModal}
        onClose={() => {
          setShowRecommendedSkillsModal(false)
          setActiveOfferForSkills(null)
        }}
        onAccept={handleAcceptRecommendedSkills}
        skills={recommendedSkillsData}
        isLoading={isSavingRecommendedSkills}
        initialWeights={{
          hard_skills: activeOfferForSkills?.ai_matching_weights?.hard_skills ?? 2,
          soft_skills: activeOfferForSkills?.ai_matching_weights?.soft_skills ?? 2,
          nice_to_have: activeOfferForSkills?.ai_matching_weights?.nice_to_have ?? 0,
        }}
      />

    </div >
  )
}
